import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function Cases() {
  const [activeTab, setActiveTab] = useState(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: '반려동물 케이스별 홉 CBD 활용 - Healing Paw Vet',
    description: '치매, 발작, 불안, 관절 통증, 피부 문제 등 반려동물의 다양한 건강 문제에 대한 홉 CBD 연구 기반 솔루션.',
    keywords: '반려동물 치매, 강아지 발작, 분리불안, 관절염, 피부 아토피, CBD 효과, 반려동물 건강 케어',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: '반려동물 케이스별 홉 CBD 활용',
      description: '치매, 발작, 불안, 관절 통증, 피부 문제 등 반려동물의 다양한 건강 문제에 대한 연구 기반 솔루션',
      url: `${siteUrl}/cases`,
      inLanguage: 'ko-KR',
      about: {
        '@type': 'MedicalCondition',
        name: '반려동물 건강 관리'
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '노령견 인지기능·치매'
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '발작·뇌전증'
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: '불안·분리불안·수면'
          },
          {
            '@type': 'ListItem',
            position: 4,
            name: '통증·관절염·염증'
          },
          {
            '@type': 'ListItem',
            position: 5,
            name: '피부·아토피·회복'
          }
        ]
      }
    }
  });

  const cases = [
    {
      id: 0,
      title: '노령견 인지기능·치매',
      icon: 'ri-brain-line',
      situation: '13살 된 우리 강아지가 최근 들어 집 안을 배회하고, 밤낮이 바뀌었어요. 이름을 불러도 반응이 없고, 화장실 실수도 잦아졌습니다.',
      symptoms: ['방향감각 상실', '밤낮 혼동', '배회 행동', '보호자 인식 저하', '화장실 실수'],
      research: '노령 동물 모델에서 CBD 투여 시 인지 기능 개선 및 신경 염증 감소가 관찰되었습니다. 특히 해마 영역의 신경세포 보호 효과가 확인되었습니다.',
      mechanism: 'CBD는 뇌의 엔도카나비노이드 시스템에 작용하여 신경 보호 효과를 나타내며, 산화 스트레스와 염증을 감소시켜 인지 기능 저하를 늦출 수 있습니다.',
      caution: '인지기능 저하는 다양한 원인으로 발생할 수 있으므로 정확한 진단이 우선입니다. 반드시 수의사와 상담 후 사용을 결정하시고, 정기적인 건강 검진을 병행하세요.'
    },
    {
      id: 1,
      title: '발작·뇌전증',
      icon: 'ri-pulse-line',
      situation: '우리 아이가 갑자기 쓰러지면서 경련을 일으켜요. 항경련제를 먹고 있지만 발작이 완전히 멈추지 않고, 약 부작용도 걱정됩니다.',
      symptoms: ['반복적인 발작', '의식 소실', '근육 경련', '침 흘림', '발작 후 혼란'],
      research: '발작 모델 실험에서 홉 CBD가 합성 CBD 및 대마 CBD와 유사하거나 더 나은 항경련 효과를 보였습니다. 발작 빈도와 강도 모두 감소했습니다.',
      mechanism: 'CBD는 신경세포의 과도한 흥분을 억제하고, GABA 수용체 활성을 증가시켜 뇌의 전기적 활동을 안정화시킵니다.',
      caution: '발작은 생명을 위협할 수 있는 증상입니다. 기존 항경련제와의 병용 시 반드시 수의사와 상담이 필요하며, 임의로 약물을 중단하지 마세요. CBD는 보조 요법으로 고려할 수 있습니다.'
    },
    {
      id: 2,
      title: '불안·분리불안·수면',
      icon: 'ri-heart-pulse-line',
      situation: '혼자 두면 계속 짖고, 물건을 물어뜯어요. 천둥 소리나 폭죽 소리에 극도로 불안해하고, 밤에도 잘 자지 못합니다.',
      symptoms: ['분리불안', '과도한 짖음', '파괴 행동', '소음 공포증', '수면 장애'],
      research: '불안 행동 평가에서 CBD 투여군이 대조군 대비 유의미한 스트레스 감소를 보였습니다. 코르티솔(스트레스 호르몬) 수치도 낮아졌습니다.',
      mechanism: 'CBD는 세로토닌 수용체(5-HT1A)에 작용하여 항불안 효과를 나타내며, 편도체의 과도한 활성을 억제하여 공포 반응을 완화합니다.',
      caution: '불안 문제는 행동 교정 훈련과 병행할 때 더 효과적입니다. 수의사 또는 행동 전문가의 조언을 함께 받으시길 권장합니다. 갑작스러운 환경 변화나 건강 문제가 원인일 수 있으니 정확한 진단이 필요합니다.'
    },
    {
      id: 3,
      title: '통증·관절염·염증',
      icon: 'ri-bone-line',
      situation: '계단을 오르내리기 힘들어하고, 산책을 싫어해요. 관절을 만지면 아파하고, 움직임이 눈에 띄게 줄었습니다.',
      symptoms: ['관절 통증', '움직임 감소', '절뚝거림', '계단 오르기 어려움', '만지면 아파함'],
      research: '관절염 모델에서 CBD 투여 시 염증 지표(IL-6, TNF-α) 감소 및 활동성 증가가 확인되었습니다. 통증 점수도 유의미하게 낮아졌습니다.',
      mechanism: 'CBD는 CB2 수용체를 통해 염증 반응을 조절하고, 통증 신호 전달을 억제하여 만성 통증을 완화합니다. 또한 연골 손상을 늦추는 효과도 있습니다.',
      caution: '관절염은 진행성 질환이므로 조기 발견과 지속적인 관리가 중요합니다. 진통제나 항염제와의 병용 시 수의사와 상담이 필요하며, 체중 관리와 적절한 운동도 함께 병행하세요.'
    },
    {
      id: 4,
      title: '피부·아토피·회복',
      icon: 'ri-heart-add-line',
      situation: '계속 긁고 핥아서 피부가 빨갛게 부어올랐어요. 털도 많이 빠지고, 상처가 잘 낫지 않습니다.',
      symptoms: ['만성 가려움', '피부 발적', '탈모', '상처 회복 지연', '핥기 행동'],
      research: '피부 염증 모델에서 CBD의 항염 및 항산화 효과가 입증되었습니다. 피부 장벽 기능 회복과 가려움 완화에도 도움이 되었습니다.',
      mechanism: 'CBD는 피부의 CB2 수용체에 작용하여 염증성 사이토카인 분비를 억제하고, 피부 세포의 재생을 촉진합니다. 또한 가려움을 유발하는 히스타민 반응을 조절합니다.',
      caution: '피부 질환의 원인은 알레르기, 기생충, 세균 감염 등 매우 다양합니다. 정확한 진단 후 사용하시고, 외용제와 병행 시 수의사와 상담하세요. 식이 관리와 환경 개선도 함께 고려해야 합니다.'
    }
  ];

  const activeCase = cases[activeTab];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-light text-gray-900 mb-6">어떤 상황에서 고려할 수 있나요</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            반려동물의 다양한 건강 문제에 대한 연구 기반 솔루션
          </p>
        </div>
      </section>

      {/* Case Tabs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-4 mb-12">
            {cases.map((item, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`p-6 border transition-all cursor-pointer ${
                  activeTab === idx 
                    ? 'border-gray-900 bg-white' 
                    : 'border-gray-200 bg-white hover:border-gray-400'
                }`}
                data-event={`case_tab_${item.id}`}
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className={`${item.icon} text-2xl ${activeTab === idx ? 'text-gray-900' : 'text-gray-600'}`}></i>
                </div>
                <div className={`text-sm font-medium ${activeTab === idx ? 'text-gray-900' : 'text-gray-600'}`}>
                  {item.title}
                </div>
              </button>
            ))}
          </div>

          {/* Case Content */}
          <div className="bg-white border border-gray-200">
            <div className="p-12">
              <div className="max-w-4xl mx-auto space-y-12">
                {/* Title */}
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center border border-gray-300 mx-auto mb-6">
                    <i className={`${activeCase.icon} text-3xl text-gray-900`}></i>
                  </div>
                  <h2 className="text-3xl font-light text-gray-900">{activeCase.title}</h2>
                </div>

                {/* Situation */}
                <div className="bg-gray-50 p-8 border-l-2 border-gray-900">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">보호자의 고민</h3>
                  <p className="text-gray-700 leading-relaxed italic">
                    "{activeCase.situation}"
                  </p>
                </div>

                {/* Symptoms */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">주요 증상</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {activeCase.symptoms.map((symptom, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-gray-900 flex-shrink-0"></div>
                        <span className="text-sm text-gray-700">{symptom}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Research */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">연구 결과</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {activeCase.research}
                  </p>
                  <div className="bg-gray-50 p-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">작용 메커니즘</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {activeCase.mechanism}
                    </p>
                  </div>
                </div>

                {/* Caution */}
                <div className="border border-gray-300 p-6">
                  <div className="flex items-start space-x-3">
                    <i className="ri-alert-line text-xl text-gray-900 flex-shrink-0 mt-1"></i>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">주의사항</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {activeCase.caution}
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="text-center pt-8 border-t border-gray-200">
                  <p className="text-gray-600 mb-6">
                    우리 아이에게 홉 CBD가 도움이 될지 궁금하신가요?
                  </p>
                  <button 
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className="px-8 py-3 bg-gray-900 text-white hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                    data-event="cta_contact_case_detail"
                  >
                    전문 상담 문의하기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-light text-gray-900 mb-4">홉 CBD 사용 시 알아두세요</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center border border-gray-300 flex-shrink-0">
                  <i className="ri-check-line text-lg text-gray-900"></i>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">수의사와 상담 필수</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    반려동물의 건강 상태, 복용 중인 약물, 기저 질환 등을 고려하여 
                    수의사와 충분히 상담 후 사용을 결정하세요.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center border border-gray-300 flex-shrink-0">
                  <i className="ri-check-line text-lg text-gray-900"></i>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">적절한 용량 설정</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    반려동물의 체중, 증상의 정도에 따라 적절한 용량이 다릅니다. 
                    낮은 용량부터 시작하여 점진적으로 조절하세요.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center border border-gray-300 flex-shrink-0">
                  <i className="ri-check-line text-lg text-gray-900"></i>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">지속적인 관찰</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    사용 후 반려동물의 행동, 식욕, 배변 상태 등을 주의 깊게 관찰하고, 
                    이상 증상이 있으면 즉시 수의사와 상담하세요.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center border border-gray-300 flex-shrink-0">
                  <i className="ri-check-line text-lg text-gray-900"></i>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">보조 요법으로 활용</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    CBD는 기존 치료를 대체하는 것이 아니라 보조하는 역할입니다. 
                    처방된 약물을 임의로 중단하지 마세요.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
